/**
 * Sets — a day, not a photo.
 *
 * A set is an ordered list of moments: where she is, what time it is, what she
 * is doing. It lives outside the characters tab but wears a character chip,
 * because a set is a RECIPE and the character is the ingredients — one set
 * written once works for every character you ever make, and remix is what
 * rebinds it.
 *
 * Every beat is a row and every shot inside it is its own row, so a beat is a
 * list rather than a number. Same room, same light, same outfit slot throughout;
 * only the action and the state move down the list, which is why an escalating
 * beat still reads as one continuous moment.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api, mediaUrl,
  type Character, type PhotoSet, type SetPreset, type SetBeat, type SetShot,
  type Place, type WardrobeSlot, type Tier, type Resolution,
} from '../lib/api';
import SetPhotos from './SetPhotos';
import './sets.css';

const TIERS: Tier[] = ['plain', 'flirty', 'suggestive', 'explicit'];
const CAMERAS = ['selfie', 'mirror', 'propped', 'handheld', 'no-face'];
/** Free text is allowed; these are the ones worth offering. */
const STATES = ['worn', 'loose', 'open', 'partly off', 'off'];

const TIER_LABEL: Record<Tier, string> = {
  plain: 'plain only', flirty: 'up to flirty',
  suggestive: 'up to suggestive', explicit: 'up to explicit',
};

function Img({ src, alt }: { src: string | null | undefined; alt: string }) {
  const [url, setUrl] = useState<string | null>(src?.startsWith('http') ? src : null);
  useEffect(() => {
    let live = true;
    if (!src) { setUrl(null); return; }
    // A FluidCloud link is just a URL — no token, no fetch, no object URL.
    if (src.startsWith('http')) { setUrl(src); return; }
    void mediaUrl(src).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [src]);
  if (!url) return <span className="ph" aria-label={alt} />;
  return <img src={url} alt={alt} />;
}

interface SetsProps {
  characters: Character[];
  onRan: () => void;
}

export default function Sets({ characters, onRan }: SetsProps) {
  const [sets, setSets] = useState<PhotoSet[]>([]);
  const [presets, setPresets] = useState<SetPreset[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterCharacter, setFilterCharacter] = useState<string>('');
  const [onlyFaceless, setOnlyFaceless] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get<{ sets: PhotoSet[] }>('/sets'),
        api.get<{ presets: SetPreset[] }>('/sets/presets'),
      ]);
      setSets(s.sets);
      setPresets(p.presets);
      setError(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not load sets'); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const open = sets.find((s) => s.id === openId) ?? null;

  const shown = sets.filter((s) =>
    (!filterCharacter || s.characterId === filterCharacter) &&
    (!onlyFaceless || s.facelessCount > 0));

  if (open) {
    return (
      <Storyboard
        set={open}
        characters={characters}
        onClose={() => setOpenId(null)}
        onChanged={async () => { await load(); }}
        onRan={onRan}
      />
    );
  }

  return (
    <div className="pad">
      <div className="page-head">
        <div>
          <h2>Sets</h2>
          <p>
            A set is a day, not a photo — an ordered list of moments: where she is, what time it is,
            what she is doing. The ones below ship with FluidDream. Change them, delete them, or write
            your own; a set is only a recipe.
          </p>
        </div>
        <div className="spacer">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>New set</button>
        </div>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="filters">
        <span className="eyebrow">Made for</span>
        <button
          className="chipf" aria-pressed={!filterCharacter}
          onClick={() => setFilterCharacter('')}
        >Everyone</button>
        {characters.map((c) => (
          <button
            key={c.id} className="chipf" aria-pressed={filterCharacter === c.id}
            onClick={() => setFilterCharacter(filterCharacter === c.id ? '' : c.id)}
          >{c.name}</button>
        ))}
        <span style={{ marginLeft: 'auto' }} />
        <button
          className="chipf" aria-pressed={onlyFaceless}
          onClick={() => setOnlyFaceless((v) => !v)}
        >Has faceless shots</button>
      </div>

      <div className="presets">
        {shown.map((s) => (
          <div key={s.id} className="preset-wrap">
            <button
              className="x rm" title="Delete"
              onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm(`Delete “${s.name}”? Images it already made are kept.`)) return;
                try { await api.del(`/sets/${s.id}`); await load(); }
                catch (err) { setError(err instanceof Error ? err.message : 'Could not delete that set'); }
              }}
            >×</button>
            <button className="preset" onClick={() => setOpenId(s.id)}>
              <span className="charchip who">
                {s.characterName ?? 'Any character'}
              </span>
              <div className="cover">
                <div className="cover-beats">
                  {s.beats.slice(0, 6).map((b) => (
                    <span key={b.id} className={b.camera === 'no-face' ? 'faint' : ''}>
                      {b.timeLabel || '—'} {b.name || b.placeName || ''}
                    </span>
                  ))}
                </div>
                <div className="tags">
                  {s.beats.some((b) => b.dayNight === 'night') && <span className="tag">night</span>}
                  {s.beats.some((b) => b.dayNight === 'day') && <span className="tag">day</span>}
                </div>
              </div>
              <div className="meta">
                <span className="nm">{s.name}</span>
                <span className="row">
                  {s.shotCount} {s.shotCount === 1 ? 'shot' : 'shots'}
                  {s.facelessCount > 0 && <><span className="sep">·</span>{s.facelessCount} faceless</>}
                </span>
                <span className="row"><span className={`tier t-${s.topTier}`}>{TIER_LABEL[s.topTier]}</span></span>
                <span className="row faint">
                  {[...new Set(s.beats.map((b) => b.placeName).filter(Boolean))].join(' · ') || 'no rooms bound yet'}
                </span>
              </div>
            </button>
          </div>
        ))}

        <button className="tile newone" onClick={() => setCreating(true)}>
          <span style={{ fontSize: 21 }}>+</span><span>New set</span>
          <span className="sub">start empty, or from<br />one of the starters</span>
        </button>
      </div>

      {shown.length === 0 && !creating && (
        <div className="notice" style={{ marginTop: 16 }}>
          {sets.length === 0
            ? 'No sets yet. Start one from a template — you can change every beat afterwards.'
            : 'No sets match that filter.'}
        </div>
      )}

      {creating && (
        <NewSet
          presets={presets}
          characters={characters}
          onCancel={() => setCreating(false)}
          onCreated={async (id) => { setCreating(false); await load(); setOpenId(id); }}
        />
      )}

      <div className="note" style={{ marginTop: 18 }}>
        <b>Faceless shots are the cheap half of a believable day.</b> A coffee, the window, shoes by
        the door. <code>no-face</code> is one of the camera modes alongside <code>selfie</code> and
        <code>mirror</code> — but it sends no identity references at all, so it costs less, finishes
        faster, cannot drift, and needs no character bound to it.
      </div>
      <div className="note" style={{ marginTop: 12 }}>
        <b>A set is a recipe; the character is the ingredients.</b> A set asks for “a bedroom” and
        “sleepwear”; pointing it at Rose binds those to <i>her</i> bedroom and <i>her</i> grey tee.
        Remix takes one you already like and rebinds it to somebody else.
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- new set --- */

function NewSet({ presets, characters, onCancel, onCreated }: {
  presets: SetPreset[];
  characters: Character[];
  onCancel: () => void;
  onCreated: (id: string) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('');
  const [characterId, setCharacterId] = useState(characters[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = presets.find((p) => p.key === preset);
  // A set of nothing but no-face beats needs no character at all.
  const needsCharacter = !chosen?.anyCharacter;

  async function create() {
    setBusy(true);
    try {
      const r = await api.post<{ set: PhotoSet }>('/sets', {
        name: name.trim() || chosen?.name || 'New set',
        preset,
        characterId: needsCharacter ? characterId : null,
        description: chosen?.description ?? '',
      });
      await onCreated(r.set.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that set');
      setBusy(false);
    }
  }

  return (
    <div className="maker" style={{ marginTop: 18 }}>
      <div className="world-head" style={{ marginBottom: 12 }}>
        <span className="eyebrow" style={{ flex: 1 }}>New set</span>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="new-set-grid">
        <label>
          <span className="eyebrow">Name</span>
          <input
            className="field" value={name} placeholder={chosen?.name ?? 'Sunday slow morning'}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          <span className="eyebrow">For</span>
          <select
            className="field" value={needsCharacter ? characterId : ''} disabled={!needsCharacter}
            onChange={(e) => setCharacterId(e.target.value)}
          >
            {!needsCharacter && <option value="">Any character</option>}
            {characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>

      <div className="routes" style={{ marginTop: 14 }}>
        <button className={`route${preset === '' ? ' sel' : ''}`} onClick={() => setPreset('')}>
          <div className="t">Start empty</div>
          <div className="d">No beats. Add them one at a time.</div>
        </button>
        {presets.map((p) => (
          <button
            key={p.key} className={`route${preset === p.key ? ' sel' : ''}`}
            onClick={() => setPreset(p.key)}
          >
            <div className="t">{p.name}</div>
            <div className="d">{p.description}</div>
            <span className="c">
              {p.beats.reduce((n, b) => n + b.shots.length, 0)} shots
              {p.anyCharacter ? ' · no identity sent' : ''}
            </span>
          </button>
        ))}
      </div>

      <button
        className="btn btn-primary" style={{ marginTop: 14 }}
        disabled={busy || (needsCharacter && !characterId)}
        onClick={() => void create()}
      >{busy ? 'Creating…' : 'Create set'}</button>
      {needsCharacter && characters.length === 0 && (
        <div className="notice bad" style={{ marginTop: 12 }}>
          Make a character first — every beat that shows her face needs one.
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- storyboard --- */

/** The editable shape. Server ids are kept so a save reads as an edit. */
type DraftShot = Omit<SetShot, 'id' | 'position'> & { key: string };
type DraftBeat = Omit<SetBeat, 'id' | 'position' | 'shots' | 'placeName'> & {
  key: string; shots: DraftShot[];
};

let keySeq = 0;
const nextKey = () => `k${++keySeq}`;

function toDraft(set: PhotoSet): DraftBeat[] {
  return set.beats.map((b) => ({
    key: nextKey(), name: b.name, placeId: b.placeId, camera: b.camera,
    outfitSlot: b.outfitSlot, timeLabel: b.timeLabel, dayNight: b.dayNight,
    light: b.light, enabled: b.enabled, note: b.note,
    shots: b.shots.map((s) => ({
      key: nextKey(), action: s.action, outfitState: s.outfitState, tier: s.tier, note: s.note,
      camera: s.camera || b.camera,
    })),
  }));
}

function Storyboard({ set, characters, onClose, onChanged, onRan }: {
  set: PhotoSet;
  characters: Character[];
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  onRan: () => void;
}) {
  const [beats, setBeats] = useState<DraftBeat[]>(() => toDraft(set));
  const [editing, setEditing] = useState<string | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [slots, setSlots] = useState<WardrobeSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<'board' | 'photos'>('board');
  const [busy, setBusy] = useState(false);
  const [resolution, setResolution] = useState<Resolution>('1k');
  const [estimate, setEstimate] = useState<{ count: number; total: number; faceless: number } | null>(null);
  const [remixTo, setRemixTo] = useState('');

  // Her rooms and her wardrobe are what the selects offer. A set bound to
  // nobody has neither, and only no-face beats make sense in it.
  useEffect(() => {
    if (!set.characterId) { setPlaces([]); setSlots([]); return; }
    void (async () => {
      try {
        const [p, w] = await Promise.all([
          api.get<{ places: Place[] }>(`/characters/${set.characterId}/places`),
          api.get<{ slots: WardrobeSlot[] }>(`/characters/${set.characterId}/wardrobe`),
        ]);
        setPlaces(p.places);
        setSlots(w.slots);
      } catch { /* the selects simply stay empty */ }
    })();
  }, [set.characterId]);

  const on = beats.filter((b) => b.enabled);
  const shotCount = on.reduce((n, b) => n + b.shots.length, 0);
  const missing = on.filter((b) => b.camera !== 'no-face' && !b.placeId).length;

  const body = useMemo(() => ({
    beats: beats.map((b) => ({
      name: b.name, placeId: b.placeId, camera: b.camera, outfitSlot: b.outfitSlot,
      timeLabel: b.timeLabel, dayNight: b.dayNight, light: b.light,
      enabled: b.enabled, note: b.note,
      shots: b.shots.map((s) => ({
        action: s.action, outfitState: s.outfitState, tier: s.tier, note: s.note,
      })),
    })),
  }), [beats]);

  function mutate(key: string, patch: Partial<DraftBeat>) {
    setBeats((bs) => bs.map((b) => (b.key === key ? { ...b, ...patch } : b)));
    setDirty(true);
  }
  function mutateShot(beatKey: string, shotKey: string, patch: Partial<DraftShot>) {
    setBeats((bs) => bs.map((b) => (b.key === beatKey
      ? { ...b, shots: b.shots.map((s) => (s.key === shotKey ? { ...s, ...patch } : s)) }
      : b)));
    setDirty(true);
  }

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/sets/${set.id}`, body);
      setDirty(false);
      await onChanged();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save this set'); }
    finally { setBusy(false); }
  }

  async function estimateRun() {
    try {
      if (dirty) await api.patch(`/sets/${set.id}`, body);
      setDirty(false);
      const r = await api.post<{ count: number; total: number; faceless: number }>(
        `/sets/${set.id}/estimate`, { resolution });
      setEstimate(r);
      setError(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not price that run'); }
  }

  async function run() {
    setBusy(true);
    try {
      if (dirty) { await api.patch(`/sets/${set.id}`, body); setDirty(false); }
      await api.post(`/sets/${set.id}/run`, { resolution });
      onRan();
      await onChanged();
      setError(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not start that run'); }
    finally { setBusy(false); }
  }

  async function remix() {
    if (!remixTo) return;
    setBusy(true);
    try {
      await api.post(`/sets/${set.id}/remix`, { characterId: remixTo });
      await onChanged();
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not remix that set'); }
    finally { setBusy(false); }
  }

  return (
    <div className="pad">
      <div className="page-head">
        <div>
          <h2>{set.name}</h2>
          <p>
            Edit any row — time, place, camera, outfit, and the shots inside it. Rows are the set;
            there is nothing else to it.
          </p>
        </div>
        <div className="spacer">
          <button className="btn" onClick={onClose}>All sets</button>
          <button className="btn btn-primary" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="sections">
        <button aria-current={tab === 'board' ? 'page' : undefined} onClick={() => setTab('board')}>
          Storyboard
        </button>
        <button aria-current={tab === 'photos' ? 'page' : undefined} onClick={() => setTab('photos')}>
          Photos
        </button>
      </div>

      {tab === 'photos' && <SetPhotos setId={set.id} onChanged={onChanged} />}

      {/* Kept mounted: the storyboard holds unsaved edits, and unmounting it to
          look at the photos would throw them away. */}
      <div className="run" style={tab === 'photos' ? { display: 'none' } : undefined}>
        <div className="card">
          <div className="world-head" style={{ marginBottom: 14 }}>
            <span className="eyebrow" style={{ flex: 1 }}>The day</span>
            {missing > 0 && (
              <span className="pill warn">
                <span className="dot" />{missing} {missing === 1 ? 'place' : 'places'} missing
              </span>
            )}
            <button
              className="btn btn-sm"
              onClick={() => {
                setBeats((bs) => [...bs, {
                  key: nextKey(), name: '', placeId: null, camera: 'selfie', outfitSlot: '',
                  timeLabel: '', dayNight: '', light: '', enabled: true, note: '',
                  shots: [{ key: nextKey(), action: '', outfitState: 'worn', camera: 'selfie', tier: 'plain', note: '' }],
                }]);
                setDirty(true);
              }}
            >Add beat</button>
          </div>

          <div className="beats">
            {beats.map((b) => {
              const faceless = b.camera === 'no-face';
              const place = places.find((p) => p.id === b.placeId);
              const top = TIERS.filter((t) => b.shots.some((s) => s.tier === t)).slice(-1)[0] ?? 'plain';
              return (
                <div key={b.key}>
                  <div className={`beat${b.enabled ? '' : ' off'}`}>
                    <span className="when">
                      <span className="time">{b.timeLabel || '—'}</span>
                      {b.dayNight && <span className="tod">{b.dayNight}</span>}
                    </span>
                    <span className={`thumb${place?.src ? '' : ' empty'}`}>
                      {place?.src ? <Img src={place.src} alt={place.name} />
                        : faceless ? 'no-face' : place ? `${place.name}\nno picture` : 'no room'}
                    </span>
                    <span>
                      <span className="nm">{b.name || 'Untitled beat'}</span>
                      <span className="sub">
                        {!faceless && (
                          <span className="chip place">
                            {place ? place.name : <>⚠ no room</>}
                          </span>
                        )}
                        <span className="chip mode">{b.camera}</span>
                        {!faceless && b.outfitSlot && (
                          <span className="chip">
                            {b.outfitSlot}
                            {b.shots[0]?.outfitState && <span className="state"> · {b.shots[0].outfitState}</span>}
                          </span>
                        )}
                      </span>
                      <span className="light">
                        {faceless ? 'no identity sent · cheaper, faster, cannot drift' : (b.light || 'no light described')}
                      </span>
                    </span>
                    <span className="ladder">
                      <span className="n">×{b.shots.length}</span>
                      <span className="steps">
                        {b.shots.map((s, i) => (
                          <span key={s.key}>
                            {i > 0 && <span className="arrow">→</span>}
                            <span className={`tier t-${s.tier}`}>{s.tier}</span>
                          </span>
                        ))}
                      </span>
                    </span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditing(editing === b.key ? null : b.key)}
                      >{editing === b.key ? 'Done' : 'Edit'}</button>
                      <button
                        className="x" title={b.enabled ? 'Switch this beat off' : 'Switch this beat on'}
                        onClick={() => mutate(b.key, { enabled: !b.enabled })}
                      >{b.enabled ? '◉' : '○'}</button>
                    </span>
                  </div>

                  {editing === b.key && (
                    <div className="editor">
                      <div className="world-head" style={{ marginBottom: 14 }}>
                        <span className="eyebrow" style={{ flex: 1 }}>
                          {b.timeLabel || '—'} · {b.name || 'Untitled beat'}
                        </span>
                        <button
                          className="btn btn-sm bad"
                          onClick={() => { setBeats((bs) => bs.filter((x) => x.key !== b.key)); setDirty(true); setEditing(null); }}
                        >Delete beat</button>
                        <button className="btn btn-sm" onClick={() => setEditing(null)}>Done</button>
                      </div>

                      <div className="egrid">
                        <label><span className="eyebrow">Name</span>
                          <input className="field" value={b.name} placeholder="Just woke up"
                            onChange={(e) => mutate(b.key, { name: e.target.value })} /></label>
                        <label><span className="eyebrow">Time</span>
                          <input className="field" value={b.timeLabel} placeholder="08:10"
                            onChange={(e) => mutate(b.key, { timeLabel: e.target.value })} /></label>
                        <label><span className="eyebrow">Day or night</span>
                          <select className="field" value={b.dayNight}
                            onChange={(e) => mutate(b.key, { dayNight: e.target.value as DraftBeat['dayNight'] })}>
                            <option value="">unsaid</option><option value="day">day</option><option value="night">night</option>
                          </select></label>
                        <label><span className="eyebrow">Camera</span>
                          <select className="field" value={b.camera}
                            onChange={(e) => mutate(b.key, { camera: e.target.value })}>
                            {CAMERAS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select></label>
                        <label><span className="eyebrow">Place</span>
                          <select className="field" value={b.placeId ?? ''}
                            onChange={(e) => mutate(b.key, { placeId: e.target.value || null })}>
                            <option value="">no room</option>
                            {places.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select></label>
                        <label><span className="eyebrow">Outfit</span>
                          <select className="field" value={b.outfitSlot} disabled={faceless}
                            onChange={(e) => mutate(b.key, { outfitSlot: e.target.value })}>
                            <option value="">none</option>
                            {slots.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                          </select></label>
                      </div>

                      <label style={{ display: 'block', marginTop: 12 }}>
                        <span className="eyebrow">Light</span>
                        <input
                          className="field" value={b.light}
                          placeholder="hard low sun, blown-out window, long shadows"
                          onChange={(e) => mutate(b.key, { light: e.target.value })}
                        />
                      </label>

                      <div className="world-head" style={{ margin: '18px 0 9px' }}>
                        <span className="eyebrow" style={{ flex: 1 }}>Shots — add one at a time</span>
                        <span className="mono faint">{b.shots.length} in this beat</span>
                      </div>

                      <div className="shots">
                        <div className="shothead">
                          <span /><span className="eyebrow">Camera</span><span className="eyebrow">Action</span>
                          <span className="eyebrow">{faceless ? '' : 'Outfit state'}</span>
                          <span className="eyebrow">Tier</span><span />
                        </div>
                        {b.shots.map((s, i) => (
                          <div key={s.key} className="shotrow">
                            <span className="sn">{i + 1}</span>
                            <select
                              className="field cam" value={s.camera || b.camera}
                              onChange={(e) => mutateShot(b.key, s.key, { camera: e.target.value })}
                            >
                              {CAMERAS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <input
                              className="field" value={s.action}
                              placeholder={faceless ? 'a mug on the counter, half drunk' : 'sitting up, hair pushed back'}
                              onChange={(e) => mutateShot(b.key, s.key, { action: e.target.value })}
                            />
                            <select
                              className="field" value={s.outfitState || 'worn'}
                              disabled={s.camera === 'no-face'}
                              onChange={(e) => mutateShot(b.key, s.key, { outfitState: e.target.value })}
                            >
                              {/* Anything typed before this was a select stays selectable. */}
                              {(STATES.includes(s.outfitState) || !s.outfitState
                                ? STATES : [s.outfitState, ...STATES]).map((st) => (
                                <option key={st} value={st}>{st}</option>
                              ))}
                            </select>
                            <span className="tiers">
                              {TIERS.map((t) => (
                                <button
                                  key={t} className={`tier t-${t}${s.tier === t ? ' sel' : ''}`}
                                  onClick={() => mutateShot(b.key, s.key, { tier: t })}
                                >{t}</button>
                              ))}
                            </span>
                            <button
                              className="x" title="Remove shot"
                              disabled={b.shots.length === 1}
                              onClick={() => {
                                mutate(b.key, { shots: b.shots.filter((x) => x.key !== s.key) });
                              }}
                            >×</button>
                          </div>
                        ))}
                        <button
                          className="addshot"
                          onClick={() => mutate(b.key, {
                            shots: [...b.shots, {
                              key: nextKey(), action: '',
                              outfitState: b.shots[b.shots.length - 1]?.outfitState ?? 'worn',
                              camera: b.shots[b.shots.length - 1]?.camera ?? b.camera,
                              tier: b.shots[b.shots.length - 1]?.tier ?? 'plain', note: '',
                            }],
                          })}
                        >+ Add shot</button>
                      </div>

                      <p className="faint editor-note">
                        Every shot is its own row, so a beat is a list rather than a number. Same room,
                        same light, same outfit slot throughout — only the action and the state change
                        down the list, which is why an escalating beat still reads as one continuous
                        moment.
                      </p>
                      <div className="note" style={{ marginTop: 14 }}>
                        <b>The tier travels with the shot.</b> It is written onto the render row, not
                        just the recipe — so the gallery can filter on it and a conversation can be
                        told what it may be sent. If it only lived on the set, the wrong photo
                        eventually reaches the wrong chat.
                      </div>
                      <span className="beat-top faint mono">highest rung in this beat: {top}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {beats.length === 0 && (
              <div className="notice">
                No beats yet. Add one — a beat is a moment: a room, an hour, a light, and the shots
                that happen in it.
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>Run</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span className="charchip">{set.characterName ?? 'Any character'}</span>
          </div>

          <dl className="kv">
            <dt>Beats on</dt><dd>{on.length} of {beats.length}</dd>
            <dt>Shots</dt><dd>{shotCount}</dd>
            <dt>Faceless</dt><dd>{on.filter((b) => b.camera === 'no-face').reduce((n, b) => n + b.shots.length, 0)}</dd>
            {missing > 0 && <><dt>Missing rooms</dt><dd className="warn">{missing}</dd></>}
          </dl>

          <label style={{ display: 'block', marginTop: 14 }}>
            <span className="eyebrow">Resolution</span>
            <select className="field" value={resolution} onChange={(e) => setResolution(e.target.value as Resolution)}>
              <option value="1k">1K — $0.045 each</option>
              <option value="2k">2K — $0.09 each</option>
            </select>
          </label>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" disabled={shotCount === 0} onClick={() => void estimateRun()}>
              What would this cost?
            </button>
            <button
              className="btn btn-primary btn-sm" disabled={busy || shotCount === 0}
              onClick={() => void run()}
            >{busy ? 'Starting…' : `Run ${shotCount} ${shotCount === 1 ? 'shot' : 'shots'}`}</button>
          </div>

          {estimate && (
            <div className="notice" style={{ marginTop: 12 }}>
              {estimate.count} images · <b>${estimate.total.toFixed(3)}</b>
              {estimate.faceless > 0 && <> · {estimate.faceless} of them send no identity</>}
            </div>
          )}

          <div className="hair" />

          <div className="eyebrow" style={{ display: 'block', margin: '14px 0 8px' }}>Remix for…</div>
          <p className="muted remix-note">
            Copies the beats onto another character. Outfit slots come across by name, so her
            “sleepwear” is found; rooms are rematched against the rooms that character actually has.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <select className="field" value={remixTo} onChange={(e) => setRemixTo(e.target.value)}>
              <option value="">pick a character</option>
              {characters.filter((c) => c.id !== set.characterId)
                .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button className="btn btn-sm" disabled={!remixTo || busy} onClick={() => void remix()}>Remix</button>
          </div>
        </div>
      </div>
    </div>
  );
}
